import { useCallback, useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";

const ALLOC_KEYS = [
  "alloc.global_equity",
  "alloc.global_equity_etf",
  "alloc.investment_grade_bonds",
  "alloc.money_market",
  "alloc.money_market_short",
  "alloc.alternative_ucits_funds",
  "alloc.alternative_ucits_strategies",
  "alloc.alternative_strategies",
  "alloc.gold_commodities",
  "alloc.commodities",
  "alloc.private_markets",
  "alloc.private_equity",
  "alloc.private_credit",
  "alloc.real_assets",
  "alloc.cash",
  "alloc.tactical_cash",
];

const EMPTY_PLAN = {
  name: "",
  description: "",
  min_amount: 25000,
  max_amount: 50000,
  duration: "12",
  gross_rate: 5.0,
  mngmt_fee: 0.5,
  perf_fee: 10.0,
  lock_period_months: 12,
  asset_allocation: [],
  volatility_target: "",
  visible_in_calculator: true,
  active: true,
};

const DURATIONS = [
  { value: "12", label: "12 mesi" },
  { value: "24", label: "24 mesi" },
  { value: "36", label: "36 mesi" },
  { value: "open", label: "Open-ended" },
];

export default function AdminPlansPage() {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | plan object

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (plan) => {
    if (!window.confirm(`Eliminare definitivamente "${plan.name}"?`)) return;
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="admin-plans-page"
      >
        <div className="flex items-baseline justify-between flex-wrap gap-4 br-fade-up">
          <div>
            <div className="br-overline">Catalogo rendimenti</div>
            <h1 className="font-serif-display text-[clamp(2.2rem,4vw,3.2rem)] mt-3">
              Pacchetti.
            </h1>
            <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px]">
              Scaglione × durata × rendimento. Definisce la matrice usata dal calcolatore
              dashboard dei nuovi clienti.
            </p>
          </div>
          <button
            onClick={() => setEditing({ ...EMPTY_PLAN, _isNew: true })}
            className="br-btn-primary"
            data-testid="plan-create-btn"
          >
            + Nuovo pacchetto
          </button>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="plans-error">
            {String(error)}
          </div>
        )}

        <div
          className="mt-8 br-card overflow-x-auto br-fade-up br-delay-1"
          data-testid="admin-plans-table"
        >
          <table className="w-full text-left">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>Nome</Th>
                <Th>Scaglione</Th>
                <Th>Durata</Th>
                <Th>Lordo atteso</Th>
                <Th>Mngmt</Th>
                <Th>Perf</Th>
                <Th>Lock</Th>
                <Th>Calc</Th>
                <Th>Stato</Th>
                <Th align="right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-5 py-12 text-center text-[color:var(--br-text-dim)] italic"
                    data-testid="plans-empty"
                  >
                    Nessun pacchetto definito. Aggiungi il primo per popolare il calcolatore.
                  </td>
                </tr>
              )}
              {plans.map((p) => (
                <tr
                  key={p.id}
                  className="border-b"
                  style={{ borderColor: "var(--br-line)" }}
                  data-testid={`plan-row-${p.id}`}
                >
                  <Td>
                    <div className="text-[color:var(--br-parchment)]">{p.name}</div>
                    {p.description && (
                      <div className="text-[0.78rem] text-[color:var(--br-text-dim)] mt-1">
                        {p.description}
                      </div>
                    )}
                  </Td>
                  <Td className="font-mono-fin">
                    {Math.round(p.min_amount / 1000)}–{Math.round(p.max_amount / 1000)}K
                  </Td>
                  <Td className="font-mono-fin">{p.duration === "open" ? "Open" : `${p.duration}m`}</Td>
                  <Td className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>
                    {p.gross_rate.toFixed(2)}%
                  </Td>
                  <Td className="font-mono-fin">{p.mngmt_fee.toFixed(2)}%</Td>
                  <Td className="font-mono-fin">{p.perf_fee.toFixed(2)}%</Td>
                  <Td className="font-mono-fin">{p.lock_period_months}m</Td>
                  <Td>
                    <span
                      className="font-mono-fin text-[0.66rem]"
                      style={{
                        color: p.visible_in_calculator
                          ? "var(--br-bronze)"
                          : "var(--br-text-dim)",
                      }}
                    >
                      {p.visible_in_calculator ? "✓" : "—"}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className="font-mono-fin text-[0.62rem] tracking-[0.18em] uppercase border px-2 py-0.5"
                      style={{
                        color: p.active ? "var(--br-bronze-soft)" : "var(--br-text-dim)",
                        borderColor: p.active ? "var(--br-bronze-soft)" : "var(--br-line-bright)",
                      }}
                    >
                      {p.active ? "Attivo" : "Archiviato"}
                    </span>
                  </Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditing({ ...p })}
                        className="br-btn-outline"
                        style={{ padding: "6px 12px", fontSize: "0.66rem" }}
                        data-testid={`plan-edit-${p.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => remove(p)}
                        className="br-btn-outline"
                        style={{
                          padding: "6px 12px",
                          fontSize: "0.66rem",
                          borderColor: "var(--br-red)",
                          color: "var(--br-red)",
                        }}
                        data-testid={`plan-delete-${p.id}`}
                      >
                        Elimina
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <PlanEditModal
          plan={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </PortalLayout>
  );
}

function PlanEditModal({ plan, onCancel, onSaved, onError }) {
  const { t } = useI18n();
  const isNew = Boolean(plan._isNew);
  const [form, setForm] = useState({
    ...plan,
    asset_allocation: Array.isArray(plan.asset_allocation)
      ? plan.asset_allocation.map((s) => ({ key: s.key, percentage: s.percentage }))
      : [],
    volatility_target: plan.volatility_target || "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const setAlloc = (idx, field) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const arr = [...(f.asset_allocation || [])];
      arr[idx] = { ...arr[idx], [field]: v };
      return { ...f, asset_allocation: arr };
    });
  };
  const addAlloc = () =>
    setForm((f) => ({
      ...f,
      asset_allocation: [
        ...(f.asset_allocation || []),
        { key: "alloc.global_equity", percentage: 0 },
      ],
    }));
  const removeAlloc = (idx) =>
    setForm((f) => ({
      ...f,
      asset_allocation: (f.asset_allocation || []).filter((_, i) => i !== idx),
    }));
  const allocTotal = (form.asset_allocation || []).reduce(
    (sum, r) => sum + (Number(r.percentage) || 0),
    0,
  );

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: String(form.name || "").trim(),
        description: String(form.description || "").trim() || null,
        tier: form.tier ?? null,
        min_amount: Number(form.min_amount),
        max_amount: Number(form.max_amount),
        duration: form.duration,
        gross_rate: Number(form.gross_rate),
        mngmt_fee: Number(form.mngmt_fee),
        perf_fee: Number(form.perf_fee),
        lock_period_months: Number(form.lock_period_months),
        visible_in_calculator: Boolean(form.visible_in_calculator),
        active: Boolean(form.active),
        asset_allocation: (form.asset_allocation || [])
          .filter((r) => r.key && r.percentage !== "" && r.percentage != null)
          .map((r) => ({ key: r.key, percentage: Number(r.percentage) })),
        volatility_target: String(form.volatility_target || "").trim() || null,
      };
      if (!payload.name || payload.name.length < 2) {
        onError("Il nome del pacchetto è obbligatorio.");
        setBusy(false);
        return;
      }
      if (payload.max_amount <= payload.min_amount) {
        onError("Il capitale massimo deve essere maggiore del minimo.");
        setBusy(false);
        return;
      }
      if (isNew) {
        await api.post("/admin/plans", payload);
      } else {
        await api.patch(`/admin/plans/${plan.id}`, payload);
      }
      onSaved();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="plan-edit-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[720px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="br-overline">{isNew ? "Nuovo pacchetto" : "Modifica pacchetto"}</div>
        <h2 className="font-serif-display text-[1.9rem] mt-3 leading-[1.1]">
          {form.name || "—"}
        </h2>

        <div className="mt-7 grid sm:grid-cols-2 gap-4">
          <PlanField
            label="Nome pacchetto"
            value={form.name}
            onChange={setField("name")}
            colSpan
            testid="plan-field-name"
          />
          <PlanField
            label="Descrizione / commenti"
            value={form.description || ""}
            onChange={setField("description")}
            colSpan
            testid="plan-field-description"
          />
          <PlanField
            label="Capitale minimo (€)"
            type="number"
            value={form.min_amount}
            onChange={setField("min_amount")}
            mono
            testid="plan-field-min"
          />
          <PlanField
            label="Capitale massimo (€)"
            type="number"
            value={form.max_amount}
            onChange={setField("max_amount")}
            mono
            testid="plan-field-max"
          />
          <label className="block sm:col-span-1">
            <span className="br-overline">Durata</span>
            <select
              value={form.duration}
              onChange={setField("duration")}
              className="mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)]"
              style={{ borderColor: "var(--br-line-bright)" }}
              data-testid="plan-field-duration"
            >
              {DURATIONS.map((d) => (
                <option key={d.value} value={d.value} style={{ background: "var(--br-midnight)" }}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <PlanField
            label="Lock period (mesi)"
            type="number"
            value={form.lock_period_months}
            onChange={setField("lock_period_months")}
            mono
            testid="plan-field-lock"
          />
          <PlanField
            label="% lorda attesa (anno)"
            type="number"
            step="0.01"
            value={form.gross_rate}
            onChange={setField("gross_rate")}
            mono
            testid="plan-field-gross"
          />
          <PlanField
            label="Management fee %"
            type="number"
            step="0.01"
            value={form.mngmt_fee}
            onChange={setField("mngmt_fee")}
            mono
            testid="plan-field-mngmt"
          />
          <PlanField
            label="Performance fee %"
            type="number"
            step="0.01"
            value={form.perf_fee}
            onChange={setField("perf_fee")}
            mono
            testid="plan-field-perf"
          />
          <PlanField
            label="Target di volatilità (es. 4-6%)"
            value={form.volatility_target || ""}
            onChange={setField("volatility_target")}
            testid="plan-field-volatility"
          />

          <div className="sm:col-span-2 mt-2" data-testid="alloc-editor">
            <div className="flex items-center justify-between">
              <span className="br-overline">Asset allocation</span>
              <span
                className="font-mono-fin text-[0.72rem]"
                style={{ color: allocTotal === 100 ? "var(--br-bronze)" : "#d98a8a" }}
                data-testid="alloc-total"
              >
                Totale: {allocTotal}%
              </span>
            </div>
            <div className="mt-3 space-y-2">
              {(form.asset_allocation || []).map((row, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2"
                  data-testid={`alloc-row-${idx}`}
                >
                  <select
                    value={row.key}
                    onChange={setAlloc(idx, "key")}
                    className="flex-1 bg-transparent border px-3 py-2 text-[0.9rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)]"
                    style={{ borderColor: "var(--br-line-bright)" }}
                    data-testid={`alloc-key-${idx}`}
                  >
                    {ALLOC_KEYS.map((k) => (
                      <option key={k} value={k} style={{ background: "var(--br-midnight)" }}>
                        {t(k)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.5"
                    value={row.percentage}
                    onChange={setAlloc(idx, "percentage")}
                    className="w-24 bg-transparent border px-3 py-2 font-mono-fin text-[0.9rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)]"
                    style={{ borderColor: "var(--br-line-bright)" }}
                    data-testid={`alloc-pct-${idx}`}
                  />
                  <span className="text-[color:var(--br-text-dim)] text-[0.85rem]">%</span>
                  <button
                    type="button"
                    onClick={() => removeAlloc(idx)}
                    className="br-btn-outline"
                    style={{ padding: "6px 10px", borderColor: "#7a4a4a", color: "#d98a8a" }}
                    data-testid={`alloc-remove-${idx}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addAlloc}
              className="br-btn-outline mt-3"
              style={{ padding: "6px 14px", fontSize: "0.66rem" }}
              data-testid="alloc-add"
            >
              + Aggiungi classe di attività
            </button>
          </div>

          <label className="sm:col-span-2 flex items-center gap-3 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.visible_in_calculator}
              onChange={setField("visible_in_calculator")}
              data-testid="plan-field-visible"
            />
            <span className="text-[0.95rem] text-[color:var(--br-parchment)]">
              Visibile nel calcolatore dashboard dei nuovi clienti
            </span>
          </label>
          <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={setField("active")}
              data-testid="plan-field-active"
            />
            <span className="text-[0.95rem] text-[color:var(--br-parchment)]">
              Pacchetto attivo (sottoscrivibile)
            </span>
          </label>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="br-btn-outline"
            data-testid="plan-cancel"
          >
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="br-btn-primary"
            data-testid="plan-save"
          >
            {busy ? "Salvataggio…" : isNew ? "Crea pacchetto" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanField({ label, value, onChange, type = "text", mono = false, colSpan = false, step, testid }) {
  return (
    <label className={`block ${colSpan ? "sm:col-span-2" : ""}`}>
      <span className="br-overline">{label}</span>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={onChange}
        className={`mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] ${
          mono ? "font-mono-fin" : ""
        }`}
        style={{ borderColor: "var(--br-line-bright)" }}
        data-testid={testid}
      />
    </label>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-4 py-3 font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td
      className={`px-4 py-3 text-[0.88rem] ${className}`}
      style={{ textAlign: align, ...style }}
    >
      {children}
    </td>
  );
}
